import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'app/hooks/useToast';
import CommentApi, { CreateCommentRequestData } from 'app/modules/comments/api';
import UserpilotUtils from 'app/utils/userpilot';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import { Editor } from '@tiptap/core';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';
import DomUtils from 'app/utils/dom';
import { usePostCommunicationContext } from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { useTranslation } from 'react-i18next';
import { CommentConvertor } from 'app/modules/comments/utils/commentConvertor';
import { FileAttachmentsExtensionStorage } from 'app/modules/textEditor/utils/getTextEditorExtenstions';
import { isUserCommentAction } from '../../utils/commentActions';
import {
  useInvalidatePostDetailQueries,
  useInvalidatePostsQueries,
} from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import { useLocation } from 'react-router-dom';
import routes from 'constants/routes';
import { CommentAnnotation } from 'app/modules/comments/types';

type UseCreateCommentProps = {
  editorRef: Editor | undefined;
  setEditorRef: (editor: Editor) => void;
  getAnnotation?: () => CommentAnnotation | null;
};

export function useCreateComment({
  editorRef,
  setEditorRef,
  getAnnotation,
}: UseCreateCommentProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const location = useLocation();
  const {
    activeContent,
    resetCommentAction,
    postId,
    commentsListRef,
    commentAction,
    comment,
    onApprovalPageSuccess,
  } = usePostCommunicationContext();
  const invalidatePostsQueries = useInvalidatePostsQueries();
  const invalidatePostDetailQueries = useInvalidatePostDetailQueries();

  const mutation = useMutation(
    async (values: CreateCommentRequestData) => {
      return CommentApi.create({
        ...values,
        action: !isUserCommentAction(values.action) ? values.action : undefined,
        parent_comment: comment?.id,
      });
    },
    {
      onSuccess: async (response, { postId, thread, action }) => {
        UserpilotUtils.track(
          thread === 'client'
            ? UserpilotUtils.events.SUBMITTED_INTERNAL_COMMENT
            : UserpilotUtils.events.SUBMITTED_CLIENT_COMMENT,
        );

        try {
          if (action) {
            toast(
              `${t('post_one')} ${t(
                `workflowStatusMovedTo.${CommentConvertor.commentActionToStatus(
                  action,
                )}`,
              )}`,
              'success',
            );

            const isApprovalAction =
              action === 'internal_approval' || action === 'client_approval';

            const isOnApprovalsPage = location.pathname.startsWith(
              routes.POSTS_APPROVALS,
            );

            if (isApprovalAction && isOnApprovalsPage) {
              onApprovalPageSuccess?.();
            }
          }
        } catch (error) {}

        await queryClient.refetchQueries(
          queryKey.postCommentsThread({
            postId,
            thread: activeContent,
          }),
        );
        invalidatePostDetailQueries({
          postId,
          commentThread: activeContent,
        });
        invalidatePostsQueries();

        onCommentCreated();
      },
      onError(e: ApiClientError) {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );

  function onCommentCreated() {
    editorRef?.commands.clearContent();
    DomUtils.scrollToBottom(commentsListRef.current, {
      delay: 100,
    });
    resetCommentAction();
  }

  function onSubmit() {
    if (editorRef) {
      const text = editorRef.getText();

      if (!text.trim()) {
        toast(t('validation.addCommentText'), 'warning');
        return;
      }

      const uploadedFiles = (
        editorRef.storage.fileAttachments as FileAttachmentsExtensionStorage
      ).getFiles();

      const annotation = getAnnotation?.() ?? null;

      mutation.mutate({
        text,
        attachments: uploadedFiles.map((file) => ({
          type: 'medium',
          medium_id: `${file.id}`,
        })),
        action: commentAction,
        meta: annotation ? { annotation } : undefined,
        postId,
        thread:
          activeContent === PostCommunicationContent.CLIENT
            ? 'client'
            : 'internal',
      });
    }
  }

  return {
    editorRef,
    setEditorRef,
    onSubmit,
    isLoading: mutation.isLoading,
  };
}
